/** Shapes of the analytics API (apps/analytics/views.py) and small formatting helpers. */

export type Range = 7 | 30 | 90;
export const RANGES: Range[] = [7, 30, 90];

export interface Trending {
  days: number;
  readers: number;
  /** Readers in the period before; null when that is older than the records kept. */
  previous_readers: number | null;
  opens: number;
  avg_read_seconds: number;
  stories: { id: number; title: string; content_type: string; slug: string | null; readers: number; opens: number; avg_read_seconds: number }[];
  daily: { day: string; readers: number; opens: number }[];
}

export interface Overview {
  days: number;
  visitors: number;
  subscribers: number;
  anonymous: number;
  new_visitors: number;
  opens: number;
  clicks: number;
  avg_read_seconds: number;
  live: number;
  countries: { country: string; country_name: string; visitors: number }[];
  devices: { device: string; visitors: number }[];
}

export interface VisitorRow {
  id: number;
  subscriber: { id: number; email: string } | null;
  ip: string | null;
  location: string;
  country: string;
  device: string;
  browser: string;
  os: string;
  first_seen: string;
  last_seen: string;
  last_path: string;
  views: number;
  clicks: number;
  read_seconds: number;
}

export interface VisitorDetail extends VisitorRow {
  user_agent: string;
  region: string;
  city: string;
  timeline: {
    day: string;
    read_seconds: number;
    views: { time: string; path: string; title: string; item: number | null; read_seconds: number; scroll: number; repeats: number; ip: string | null; city: string }[];
    clicks: { time: string; path: string; target: string; label: string }[];
  }[];
}

export interface Live {
  minutes: number;
  count: number;
  pages_open: number;
  visitors: VisitorRow[];
}

/** 0 → "0s", 75 → "1m 15s", 3700 → "1h 1m". */
export function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export const visitorName = (v: Pick<VisitorRow, "id" | "subscriber">) => v.subscriber?.email ?? `Anonymous #${v.id}`;

export const deviceLabel = (v: Pick<VisitorRow, "device" | "browser" | "os">) =>
  [v.device && v.device[0]!.toUpperCase() + v.device.slice(1), v.browser, v.os].filter(Boolean).join(" · ");
